import { useEffect, useState } from 'react'

import helpers from 'helpers'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'

const useNotificationPreferences = ({
  NOTIFICATION_FREQUENCIES,
  automaticPreferenceKeyFor,
}) => {
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [preferences, setPreferences] = useState({})
  const [companyPreferences, setCompanyPreferences] = useState({})
  const [automaticPreferences, setAutomaticPreferences] = useState({
    mine: {},
    company: {},
  })

  const snackbar = useSnackbar()

  const handleChange = ({ rowId, columnId, checked }) => {
    setPreferences((previous) => {
      const row = { ...(previous[rowId] || {}) }

      if (columnId === 'disabled') {
        row.disabled = checked
        if (checked) {
          NOTIFICATION_FREQUENCIES.forEach((frequency) => {
            row[frequency] = false
          })
        }
      } else {
        row[columnId] = checked
        if (checked) row.disabled = false
      }

      return { ...previous, [rowId]: row }
    })
  }

  const handleCompanyChange = ({ rowId, columnId, checked }) => {
    setCompanyPreferences((previous) => {
      const row = { ...(previous[rowId] || {}) }

      if (columnId === 'disabled') {
        row.disabled = checked
        if (checked) {
          NOTIFICATION_FREQUENCIES.forEach((frequency) => {
            row[frequency] = false
          })
        }
      } else {
        row[columnId] = checked
        if (checked) row.disabled = false
      }

      return { ...previous, [rowId]: row }
    })
  }

  const handleAutomaticChange = ({ rowId, columnId, checked }) => {
    const preferenceKey = automaticPreferenceKeyFor(rowId)
    setAutomaticPreferences((previous) => ({
      ...previous,
      [columnId]: { ...previous[columnId], [preferenceKey]: checked },
    }))
  }

  const bulkNotificationRows = (enabled, events) => {
    const next = {}
    events.forEach((event) => {
      next[event.id] = {
        disabled: !enabled,
        instant: enabled,
        daily: enabled,
        weekly: enabled,
      }
    })
    return next
  }

  const handleToggleAllNotifications = ({
    tab,
    tabs,
    enabling,
    allowedAutomaticEvents,
    hasOrganizationTab,
    events,
  }) => {
    if (tab === tabs.MINE) {
      setPreferences(bulkNotificationRows(enabling, events))
    } else if (tab === tabs.COMPANY) {
      setCompanyPreferences(bulkNotificationRows(enabling, events))
    } else if (tab === tabs.AUTOMATIC) {
      setAutomaticPreferences((previous) => {
        const next = {
          mine: { ...previous.mine },
          company: { ...previous.company },
        }
        allowedAutomaticEvents.forEach((event) => {
          const key = automaticPreferenceKeyFor(event.id)
          next.mine[key] = enabling
          if (hasOrganizationTab) next.company[key] = enabling
        })
        return next
      })
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await service.dponet.kanbanConfigurations.update({
        notificationPreferences: {
          mine: preferences,
          company: companyPreferences,
          automaticMine: automaticPreferences.mine,
          automaticCompany: automaticPreferences.company,
        },
      })
      snackbar.open({
        message: 'Configurações de notificação salvas com sucesso.',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    let active = true

    const fetchConfiguration = async () => {
      try {
        const { data } = await service.dponet.kanbanConfigurations.get()
        if (!active) return
        const notificationPreferences =
          data?.kanbanConfiguration?.notificationPreferences || {}
        setPreferences(notificationPreferences.mine || {})
        setCompanyPreferences(notificationPreferences.company || {})
        setAutomaticPreferences({
          mine: notificationPreferences.automaticMine || {},
          company: notificationPreferences.automaticCompany || {},
        })
      } catch (error) {
        if (active) {
          snackbar.open({
            message: helpers.formatters.errorMessage(error),
            variant: 'error',
          })
        }
      } finally {
        if (active) setLoading(false)
      }
    }

    fetchConfiguration()

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    saving,
    loading,
    preferences,
    companyPreferences,
    automaticPreferences,
    handleChange,
    handleCompanyChange,
    handleAutomaticChange,
    handleToggleAllNotifications,
    handleSave,
  }
}

export default useNotificationPreferences
